import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    alpha,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

const DeleteConfirmDialog = ({ open, onClose, target, onConfirm, colors, t }) => {
    const [hardDelete, setHardDelete] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!target) return null;

    const isOrg = target.type === 'org';
    const itemName = isOrg ? target.item.name : target.item.email;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            if (isOrg) {
                await onConfirm(hardDelete);
            } else {
                await onConfirm();
            }
        } finally {
            setLoading(false);
            setHardDelete(false);
        }
    };

    const handleClose = () => {
        setHardDelete(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {isOrg ? t('delete_organization', 'Delete Organization') : t('delete_user', 'Delete User')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <DialogContentText sx={{ color: colors.textSecondary }}>
                    {isOrg
                        ? t('delete_org_confirm', `Are you sure you want to delete "${itemName}"? This will deactivate all users and cancel the subscription.`)
                        : t('delete_user_confirm', `Are you sure you want to delete "${itemName}"? The user will be deactivated.`)
                    }
                </DialogContentText>

                {isOrg && (
                    <Box sx={{ mt: 2, p: 2, borderRadius: '10px', bgcolor: alpha(colors.error, 0.06), border: `1px solid ${alpha(colors.error, 0.15)}` }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hardDelete}
                                    onChange={(e) => setHardDelete(e.target.checked)}
                                    sx={{ color: colors.error, '&.Mui-checked': { color: colors.error } }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.error }}>
                                        {t('permanent_delete', 'Permanently delete')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                        {t('permanent_delete_org_warning', 'This will permanently remove the organization, all users, and subscriptions from the database. This action cannot be undone.')}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                )}

                {hardDelete && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: '8px', bgcolor: alpha(colors.warning, 0.1) }}>
                        <Warning sx={{ color: colors.warning, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: colors.warning }}>
                            {t('irreversible_action', 'This action is irreversible!')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={handleClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    sx={{
                        bgcolor: hardDelete ? colors.error : colors.primary,
                        color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: hardDelete ? alpha(colors.error, 0.85) : colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(hardDelete ? colors.error : colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading
                        ? t('deleting', 'Deleting...')
                        : hardDelete
                            ? t('permanently_delete', 'Permanently Delete')
                            : t('deactivate', 'Deactivate')
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmDialog;
